// Plain constants, labels and pure functions, kept out of `schema.ts` because `pgTable()` runs at
// module scope and cannot be tree-shaken. Nothing here may import `./schema` or drizzle; a test
// enforces it.
import { CODE_STATUSES } from "./care/resident-safety";

void CODE_STATUSES;

export * from "./enums-ats";
export * from "./enums-therapy";

// The path a new hire walks between being converted from a candidate and being
// fully onboarded. Ordered: the tracker renders them in this sequence.
export const ONBOARDING_STATUSES = [
  "new_hire",
  "courses_incomplete",
  "courses_complete",
  "waiting_on_license",
  "setting_availability",
  "availability_set",
  "evaluation_scheduled",
  "pre_eval_check_in",
  "post_eval_check_in",
  "first_eval_signed",
  "onboarding_complete",
  // Not positions on the path — a hire who has stalled on it. Kept in the same
  // column because they answer the same question: where is this person up to.
  "on_hold",
  "nonresponsive",
] as const;

export type OnboardingStatus = (typeof ONBOARDING_STATUSES)[number];

export const ONBOARDING_STATUS_LABELS: Record<OnboardingStatus, string> = {
  new_hire: "New hire",
  courses_incomplete: "Training outstanding",
  courses_complete: "Training complete",
  waiting_on_license: "Waiting on licence",
  setting_availability: "Setting availability",
  availability_set: "Availability set",
  evaluation_scheduled: "Evaluation scheduled",
  pre_eval_check_in: "Pre-evaluation check-in",
  post_eval_check_in: "Post-evaluation check-in",
  first_eval_signed: "First evaluation signed",
  onboarding_complete: "Onboarding complete",
  on_hold: "On hold",
  nonresponsive: "Not responding",
};

export const ORDER_TYPES = ["medication", "treatment", "vital_sign", "info"] as const;

export const ADMINISTRATION_STATUSES = [
  "given",
  "refused",
  "held",
  "out_of_facility",
  "not_available",
  "self_administered",
  "wasted",
  "missed",
  "acknowledged",
  "other",
] as const;

// Vitals capturable at administration time (med orders may require a subset;
// vital-sign orders record the configured set).
export const VITAL_KEYS = ["BG", "BP", "PULSE", "O2", "TEMP", "RESP", "WEIGHT", "PAIN"] as const;

// Injection/application site codes (printed MAR body-site key).
export const PRN_FOLLOWUP_RESULTS = [
  "effective",
  "not_effective",
  "partially_effective",
  "unable_to_assess",
] as const;

export type PrnFollowUpResult = (typeof PRN_FOLLOWUP_RESULTS)[number];

export const PRN_FOLLOWUP_RESULT_LABELS: Record<PrnFollowUpResult, string> = {
  effective: "Effective",
  not_effective: "Not effective",
  partially_effective: "Partially effective",
  unable_to_assess: "Unable to assess",
};

// How a low order gets resupplied — drives the refill-queue action per order.
export const REFILL_METHODS = [
  "pharmacy_auto",
  "call_pharmacy",
  "md_order_no_visit",
  "md_order_visit",
] as const;

// Supply ledger event kinds: deliveries add, counts set, adjustments correct.
export const SUPPLY_EVENT_KINDS = [
  "received_full",
  "received_short",
  "count_set",
  "adjustment",
] as const;

export type SupplyEventKind = (typeof SUPPLY_EVENT_KINDS)[number];

export const APPOINTMENT_TYPES = [
  "medical",
  "specialist",
  "dental",
  "vision",
  "audiology",
  "therapy",
  "lab",
  "imaging",
  "dialysis",
  "procedure",
  "salon",
  "social",
  "religious",
  "other",
] as const;

export const TRANSPORT_MODES = [
  "facility",
  "family",
  "medical_transport",
  "self",
  "other",
] as const;

export const MOBILITY_NEEDS = [
  "wheelchair",
  "walker",
  "oxygen",
  "stretcher",
  "escort_required",
] as const;

// Scopes an external API key may hold. Extend this list as more surfaces are
// exposed (e.g. "leads:read"); scopes are checked per-route by requireApiScope.
export const API_KEY_SCOPES = ["residents:read", "notes:write"] as const;

export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];

// Institution types for the directory: THE canonical list, from which the Postgres enum, insert
// schema and both forms derive. A new value also needs its own hand-written `ALTER TYPE ... ADD
// VALUE IF NOT EXISTS` migration, since an enum cannot be widened by a column change.
export const institutionTypes = [
  "hospital",
  "skilled_nursing",
  "assisted_living",
  "independent_living",
  "hospice",
  "home_health",
  "pharmacy",
  "funeral_home",
  "clinic",
  // Insurers and public payers (Medicaid, Medicare, MCOs) recur in residents' insurance rows and
  // get faxed IDs and authorizations. Listed before "other" for the dropdown; the Postgres enum has
  // it last (appended).
  "payer",
  "other",
] as const;

// One signing-team slot in `payload.signingTeam`. The Assessor (`payload.assessorId`) is ALWAYS a
// required signer. The roster in `assessment_service_plan_signers` is derived at Complete & Send: a
// person holding several slots gets ONE row with a combined label.
export const ASP_RESIDENT_SIGNER_ROLES = [
  { key: "resident", label: "Resident" },
  { key: "responsible_party", label: "Resident Responsible Party" },
] as const;

// family_links.signer_role is free text defaulting to responsible_party, so a link can hold
// a value no plan has a slot for. Narrowing here answers both questions: may this login be offered,
// and under what title.
export function aspResidentSignerRole(
  value: string | null | undefined,
): (typeof ASP_RESIDENT_SIGNER_ROLES)[number] | null {
  return ASP_RESIDENT_SIGNER_ROLES.find((role) => role.key === value) ?? null;
}
